/**
 * Фразы-ссылки на материалы в тексте промпта и их разбор — общий модуль для сайта и
 * админки (мастер — shared/prompt-vars, копии кладёт scripts/sync_prompt_vars.sh).
 *
 * Текст промпта хранит КАНОНИЧЕСКИЕ фразы из spec.json («Image 1», «the reference video»);
 * редактор рисует их пилюлями, сервер переводит в синтаксис провайдера (@Image1 / @image_1).
 * Шаблоны карточек в админке хранят ТОКЕНЫ ({photo1}, {video}); сайт подставляет вместо них
 * те же фразы при применении карточки. Здесь — разбор обоих видов текста в одни и те же
 * PillToken, чтобы один редактор работал в обоих режимах.
 */
import {
	PROMPT_VARS_SPEC,
	legacyPhotoPhrases,
	parseToken,
	promptVar,
	providerToken,
	tokenSplitRegex,
	varMax,
	varPhrase,
	varToken,
	type PromptChannel,
	type PromptKind,
	type PromptRefStyle,
	type PromptVarKey,
} from './index'

export type { PromptKind, PromptRefStyle }

const photoPhrases = (kind: PromptKind): string[] => Array.from({ length: varMax('photo') }, (_, i) => varPhrase('photo', kind, i + 1))
/** Техническое имя фото «Image N» (владелец 04.09, как у Kling) — одно для фото- и видео-моделей. */
export const PHOTO_PHRASES: Record<PromptKind, string[]> = { image: photoPhrases('image'), video: photoPhrases('video') }
// Старые фразы (до 04.09) распознаются как те же пилюли — сохранённые вариации и
// набранные промпты не ломаются; rewritePromptKind на сайте переводит их в новое имя.
const LEGACY_PHOTO_PHRASES: string[][] = legacyPhotoPhrases()

export const FIRST_FRAME_PHRASES: Record<PromptKind, string> = {
	image: varPhrase('first_frame', 'image'),
	video: varPhrase('first_frame', 'video'),
}
export const LAST_FRAME_PHRASE = varPhrase('last_frame')
export const CHARACTER_PHRASE = varPhrase('character')
export const VIDEO_REF_PHRASE = varPhrase('video')
export const AUDIO_REF_PHRASE = varPhrase('audio')

export type PillKind = 'photo' | 'first_frame' | 'last_frame' | 'character' | 'video_ref' | 'audio_ref' | 'variant' | 'value'

export interface PillToken {
	/** Текст в промпте: каноническая фраза (режим «фразы») или токен {photo2} (режим «шаблон»). */
	phrase: string
	/** 'asset' — упоминание ассета сцены (токен @{uuid}@), не переменная спеки. */
	kind: PillKind | 'asset'
	/** Для photo: ранг 1..4 (позиция в референсах). */
	rank?: number
	/** Для asset: id ассета из токена. */
	assetId?: string
}

// ── Упоминания ассетов сцены (персонаж / локация / предмет) ──────────────────────────────────
// Чип ассета в редакторе = токен @{uuid}@ в тексте (разделитель как у Artlist). Сервер переводит его в
// «Имя (Image N)» по номерам фото ассета в запросе (backend/app/utils/asset_refs.py), дальше работает общий
// перевод фраз в синтаксис модели (@Image3 / @image_3). Ассета нет среди доступных — токен вырезается сервером.
export const ASSET_TOKEN_SRC = '@\\{[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\\}@'
const ASSET_ID_RE = /^@\{([0-9a-fA-F-]{36})\}@$/

export const assetToken = (id: string): string => `@{${id.toLowerCase()}}@`

export function assetIdOf(token: string): string | null {
	const m = ASSET_ID_RE.exec(token)
	return m ? m[1].toLowerCase() : null
}

/** id ассетов, упомянутых в тексте, — по порядку первого упоминания. */
export function assetIdsInPrompt(text: string): string[] {
	const out: string[] = []
	for (const m of (text || '').matchAll(new RegExp(ASSET_TOKEN_SRC, 'g'))) {
		const id = assetIdOf(m[0])
		if (id && !out.includes(id)) out.push(id)
	}
	return out
}

/** Убрать упоминания ассетов (все или перечисленных) и подчистить пробелы вокруг вырезанного. */
export function stripAssetTokens(text: string, ids?: string[]): string {
	const drop = ids ? new Set(ids.map((i) => i.toLowerCase())) : null
	const out = (text || '').replace(new RegExp(ASSET_TOKEN_SRC, 'g'), (tok) => {
		const id = assetIdOf(tok)
		return !drop || (id && drop.has(id)) ? '' : tok
	})
	return out.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([,.;:!?])/g, '$1').replace(/^[ \t]+|[ \t]+$/gm, '')
}

/** Куда встаёт каретка после замены текста извне (before → after): правка левее каретки сдвигает её на разницу
 *  длин, правка вокруг каретки ставит её сразу за изменённым куском, правка правее каретку не трогает. */
export function mapCaret(before: string, after: string, caret: number): number {
	const max = Math.min(before.length, after.length)
	let p = 0
	while (p < max && before[p] === after[p]) p++
	if (caret <= p) return caret
	let s = 0
	while (s < max - p && before[before.length - 1 - s] === after[after.length - 1 - s]) s++
	if (caret >= before.length - s) return caret + after.length - before.length
	return after.length - s
}

/** Ключ спеки ↔ вид пилюли. */
export const KIND_BY_KEY: Record<PromptVarKey, PillKind> = {
	photo: 'photo', first_frame: 'first_frame', last_frame: 'last_frame', character: 'character',
	video: 'video_ref', audio: 'audio_ref', variant: 'variant', value: 'value',
}
export const KEY_BY_KIND: Record<PillKind, PromptVarKey> = {
	photo: 'photo', first_frame: 'first_frame', last_frame: 'last_frame', character: 'character',
	video_ref: 'video', audio_ref: 'audio', variant: 'variant', value: 'value',
}

// Список для распознавания: ДЛИННЫЕ фразы раньше коротких («the first reference
// image» должна матчиться раньше «the first image» — иначе решётся на куски).
const ALL_TOKENS: PillToken[] = [
	...PHOTO_PHRASES.image.map((phrase, i) => ({ phrase, kind: 'photo' as const, rank: i + 1 })),
	...LEGACY_PHOTO_PHRASES.flatMap((arr) => arr.map((phrase, i) => ({ phrase, kind: 'photo' as const, rank: i + 1 }))),
	{ phrase: CHARACTER_PHRASE, kind: 'character' as const },
	{ phrase: VIDEO_REF_PHRASE, kind: 'video_ref' as const },
	{ phrase: AUDIO_REF_PHRASE, kind: 'audio_ref' as const },
	{ phrase: FIRST_FRAME_PHRASES.video, kind: 'first_frame' as const },
	{ phrase: LAST_FRAME_PHRASE, kind: 'last_frame' as const },
	{ phrase: FIRST_FRAME_PHRASES.image, kind: 'first_frame' as const },
].sort((a, b) => b.phrase.length - a.phrase.length)

const TOKEN_BY_PHRASE = new Map(ALL_TOKENS.map((t) => [t.phrase.toLowerCase(), t]))

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
// Границы слов — чтобы «the first image» не матчился внутри «the first imagery».
export const PHRASE_RE = new RegExp(`(?<![\\p{L}\\p{N}])(?:${ALL_TOKENS.map((t) => esc(t.phrase)).join('|')})(?![\\p{L}\\p{N}])`, 'giu')

export function tokenForPhrase(phrase: string): PillToken | undefined {
	return TOKEN_BY_PHRASE.get(phrase.toLowerCase())
}

export interface PromptPart {
	type: 'text' | 'pill'
	value: string
	token?: PillToken
}

// Разбор промпта: упоминания ассетов и фразы-пилюли одним проходом.
const SPLIT_RE = new RegExp(`${ASSET_TOKEN_SRC}|${PHRASE_RE.source}`, 'giu')

/** Разбивка текста промпта на куски: обычный текст, фразы-пилюли и упоминания ассетов. */
export function splitPromptByPhrases(text: string): PromptPart[] {
	const out: PromptPart[] = []
	let last = 0
	for (const m of (text || '').matchAll(SPLIT_RE)) {
		const i = m.index ?? 0
		if (i > last) out.push({ type: 'text', value: text.slice(last, i) })
		const assetId = assetIdOf(m[0])
		out.push({
			type: 'pill',
			value: m[0],
			token: assetId ? { phrase: m[0], kind: 'asset', assetId } : tokenForPhrase(m[0]),
		})
		last = i + m[0].length
	}
	if (last < (text || '').length) out.push({ type: 'text', value: text.slice(last) })
	return out
}

export function hasPillPhrases(text: string): boolean {
	PHRASE_RE.lastIndex = 0
	return PHRASE_RE.test(text || '')
}

/** Фраза для вставки по рангу/типу (кнопки «Сослаться на фото»). */
export function phraseForRank(rank: number, kind: PromptKind): string | null {
	return PHOTO_PHRASES[kind][rank - 1] ?? null
}

/** Токен, который модель получит вместо пилюли; null — у модели своего синтаксиса нет
 *  (или у этого входа нет id: кадры, персонаж, аудио у Kling). */
export function refTokenLabel(token: Pick<PillToken, 'kind' | 'rank'>, style: PromptRefStyle | null | undefined): string | null {
	if (token.kind === 'photo') return providerToken('image', token.rank ?? 1, style)
	if (token.kind === 'video_ref') return providerToken('video', 1, style)
	if (token.kind === 'audio_ref') return providerToken('audio', 1, style)
	return null
}

// ── Режим «шаблон» (конструктор карточек): токены {photoN}, {video}, {variant}… ──

/** Токен шаблона → пилюля: '{photo2}' → { phrase: '{photo2}', kind: 'photo', rank: 2 }. */
export function tokenFromTemplate(token: string): PillToken | undefined {
	const parsed = parseToken(token)
	if (!parsed) return undefined
	return { phrase: token, kind: KIND_BY_KEY[parsed.key], rank: parsed.key === 'photo' ? parsed.n : undefined }
}

const TEMPLATE_SPLIT_RE = tokenSplitRegex('admin')

/** Разбивка шаблона карточки на текст и токены-пилюли. */
export function splitPromptByTokens(text: string): PromptPart[] {
	const out: PromptPart[] = []
	for (const part of (text || '').split(TEMPLATE_SPLIT_RE)) {
		if (!part) continue
		const token = tokenFromTemplate(part)
		out.push(token ? { type: 'pill', value: part, token } : { type: 'text', value: part })
	}
	return out
}

/** Токен шаблона для пилюли (кнопки вставки в админке): photo → '{photoN}'. */
export function templateTokenFor(kind: PillKind, rank?: number): string {
	return varToken(KEY_BY_KIND[kind], rank)
}

/** Каноническая фраза, в которую сайт превратит токен шаблона. */
export function phraseForToken(token: PillToken, kind: PromptKind): string | null {
	if (token.kind === 'asset') return null
	const key = KEY_BY_KIND[token.kind]
	const def = PROMPT_VARS_SPEC.vars.find((v) => v.key === key)
	if (!def?.phrase) return null
	return varPhrase(key, kind, token.rank)
}

// ── Ссылки, записанные «руками» (владелец 28.09: «сделай так, чтобы было очевидно и понятно, как это работает») ──
// Сервер (backend/app/utils/prompt_refs.py) узнаёт любую запись ссылки на материал — @image1, @Image1, @image_1,
// image 1, <<<image_1>>>, «the first image», «the reference video» — и переводит её в синтаксис модели, но только у
// моделей со своим синтаксисом (Seedance, Kling); остальным «@video1» уходит буквально. Редактор узнаёт те же записи
// ТЕМ ЖЕ правилом и сводит их к канонической форме своего режима: в шаблоне карточки — к переменной ({photo1},
// {video}), в промпте сайта и мастерской — к фразе («Image 1», «the reference video»). Дальше каждая модель получает
// свой формат: Seedance — @Image1, Kling — @image_1, остальные — «Image 1» словами.

const spaced = (s: string) => esc(s).replace(/ /g, '\\s+')
/** Каналы, у которых хоть у одного провайдера есть токен (image, video, audio) — как channels() сервера. */
const REF_CHANNELS: PromptChannel[] = Array.from(new Set(Object.values(PROMPT_VARS_SPEC.styles).flatMap((st) => Object.keys(st) as PromptChannel[])))
const LEGACY_TEMPLATES: string[] = promptVar('photo').legacy_phrases ?? []
const NOT_AFTER_WORD = '(?<![\\p{L}\\p{N}_])'
const NOT_BEFORE_WORD = '(?![\\p{L}\\p{N}_])'

/** Зеркало _build_token_re сервера: те же записи и те же границы слов (\w у Python — буквы, цифры и «_»). */
const REF_RE = (() => {
	const alts = [`(?<![\\p{L}\\p{N}_@])@?(?<kind>${REF_CHANNELS.map(esc).join('|')})[ _]?(?<num>\\d{1,2})${NOT_BEFORE_WORD}`]
	const ords = PROMPT_VARS_SPEC.ordinals.map(esc).join('|')
	LEGACY_TEMPLATES.forEach((tpl, i) => {
		alts.push(`${NOT_AFTER_WORD}${spaced(tpl).replace(esc('{ordinal}'), `(?<ord${i}>${ords})`)}${NOT_BEFORE_WORD}`)
	})
	for (const key of ['video', 'audio'] as const) alts.push(`${NOT_AFTER_WORD}(?<ref_${key}>${spaced(varPhrase(key))})${NOT_BEFORE_WORD}`)
	// Необязательные скобки «<<< … >>>» вокруг записи.
	return new RegExp(`(?<open><{2,3}\\s*)?(?:${alts.join('|')})(?<close>\\s*>{2,3})?`, 'giu')
})()

/** Материал, на который ссылается запись: канал и номер (как их видит сервер). */
export interface MaterialRef {
	channel: PromptChannel
	n: number
}

function refOfMatch(m: RegExpMatchArray): MaterialRef | null {
	const g = m.groups || {}
	if (g.kind) return { channel: g.kind.toLowerCase() as PromptChannel, n: Number(g.num) }
	for (let i = 0; i < LEGACY_TEMPLATES.length; i++) {
		const o = g[`ord${i}`]
		if (o) return { channel: 'image', n: PROMPT_VARS_SPEC.ordinals.indexOf(o.toLowerCase()) + 1 }
	}
	if (g.ref_video) return { channel: 'video', n: 1 }
	if (g.ref_audio) return { channel: 'audio', n: 1 }
	return null
}

/** Переменная для ссылки; null — переменной нет (второе видео, пятое фото): запись остаётся текстом, и сервер
 *  переведёт её сам, если такой материал в запросе окажется. */
function pillOfRef(ref: MaterialRef): { kind: PillKind; rank?: number } | null {
	if (ref.channel === 'image') return ref.n >= 1 && ref.n <= varMax('photo') ? { kind: 'photo', rank: ref.n } : null
	if (ref.n !== 1) return null
	return ref.channel === 'video' ? { kind: 'video_ref' } : ref.channel === 'audio' ? { kind: 'audio_ref' } : null
}

export interface RefConversion {
	/** Запись в тексте до замены: «@video1», «<<<image_1>>>». */
	from: string
	/** Во что превратилась: переменная ({video}) или фраза (the reference video). */
	to: string
	kind: PillKind
	rank?: number
}

export interface RefScan {
	/** Текст, где записи «руками» заменены канонической формой режима. */
	text: string
	converted: RefConversion[]
	/** Записи ссылок без своей переменной — остаются текстом как есть. */
	kept: Array<MaterialRef & { text: string }>
	/** Позиция в исходном тексте → позиция в новом (каретка после замены). */
	mapPos: (pos: number) => number
}

/**
 * Свести ссылки, записанные «руками», к канонической форме режима редактора: «template» (конструктор карточек) —
 * к переменным {photoN} / {video} / {audio}, «phrase» (сайт, мастерская) — к фразам «Image N» / «the reference video».
 * В режиме фраз уже распознанные фразы («Image 1», «the first image») не трогаются — они и так пилюли.
 * keepAt — позиция каретки: запись, которая кончается ровно на ней, ещё набирается и остаётся как есть.
 */
export function normalizeRefs(text: string, mode: 'phrase' | 'template', kind: PromptKind = 'image', keepAt?: number): RefScan {
	const src = text || ''
	const edits: Array<{ start: number; end: number; to: string }> = []
	const converted: RefConversion[] = []
	const kept: RefScan['kept'] = []
	for (const m of src.matchAll(REF_RE)) {
		const start = m.index ?? 0
		const end = start + m[0].length
		const ref = refOfMatch(m)
		if (!ref) continue
		const pill = pillOfRef(ref)
		if (!pill) {
			kept.push({ ...ref, text: m[0] })
			continue
		}
		if (mode === 'phrase' && tokenForPhrase(m[0])) continue
		if (keepAt != null && end === keepAt) continue
		const to = mode === 'template' ? templateTokenFor(pill.kind, pill.rank) : phraseForToken({ phrase: '', ...pill }, kind)
		if (!to || to === m[0]) continue
		edits.push({ start, end, to })
		converted.push({ from: m[0], to, ...pill })
	}
	let out = ''
	let last = 0
	for (const e of edits) {
		out += src.slice(last, e.start) + e.to
		last = e.end
	}
	out += src.slice(last)
	const mapPos = (pos: number) => {
		let shift = 0
		for (const e of edits) {
			if (pos >= e.end) shift += e.to.length - (e.end - e.start)
			else if (pos > e.start) return e.start + shift + e.to.length
			else break
		}
		return pos + shift
	}
	return { text: out, converted, kept, mapPos }
}

/** Все ссылки на материалы в тексте ровно так, как их поймёт сервер: канал, номер и запись. */
export function materialRefsIn(text: string): Array<MaterialRef & { text: string }> {
	const out: Array<MaterialRef & { text: string }> = []
	for (const m of (text || '').matchAll(REF_RE)) {
		const ref = refOfMatch(m)
		if (ref) out.push({ ...ref, text: m[0] })
	}
	return out
}

